import { useState } from "react";

const EMPTY_BUSINESS = { name: "", website: "", postcode: "", lat: null, lng: null, places: [] };

// Everything typed into the search page. Lives above the screens, so the
// page keeps its values when you leave and come back.
// keywords: raw text as typed.
// Locations: the business postcode (in business) is the first business
// location; sites are the others (multi-site, franchises):
//   [{ key, text, place: { postcode, lat, lng, places } or null until found }].
// radius: miles, or null = no radius (just each location's own district).
// shuffle: how many times Shuffle was pressed. removedPicks: radius picks
// taken off with ×. postcodes: the extra postcodes typed in, on top of the
// picks: [{ postcode, lat, lng, places }].
// nicheChoice: null = use the niche worked out from the keywords;
//   { niche } = picked with Change; { suggestion: { id, text } } = suggested (runs as Unsorted).
export default function useScanForm() {
  // "search" (typed in) or "csv" (Upload CSV tab).
  const [mode, setMode] = useState("search");
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [postcodes, setPostcodes] = useState([]);
  const [sites, setSites] = useState([]);
  const [radius, setRadius] = useState(null);
  const [shuffle, setShuffle] = useState(0);
  const [removedPicks, setRemovedPicks] = useState([]);
  const [nicheChoice, setNicheChoice] = useState(null);
  const [business, setBusiness] = useState(EMPTY_BUSINESS);
  const [samples, setSamples] = useState(3);
  const [spacing, setSpacing] = useState("hour");

  // A niche picked with Change only lasts until the keywords change;
  // then it's worked out again from the new keywords.
  const addKeyword = (text) => {
    const raw = String(text ?? keywordInput).trim().replace(/\s+/g, " ");
    if (raw && !keywords.some(k => k.toLowerCase() === raw.toLowerCase())) {
      setKeywords(p => [...p, raw]);
      setNicheChoice(null);
    }
    setKeywordInput("");
  };
  const addKeywords = (list) => {
    setNicheChoice(null);
    setKeywords(p => {
      const seen = new Set(p.map(k => k.toLowerCase()));
      const extra = [];
      for (const item of list) {
        const raw = String(item).trim().replace(/\s+/g, " ");
        if (raw && !seen.has(raw.toLowerCase())) {
          seen.add(raw.toLowerCase());
          extra.push(raw);
        }
      }
      return [...p, ...extra];
    });
  };
  const removeKeyword = raw => {
    setKeywords(p => p.filter(k => k !== raw));
    setNicheChoice(null);
  };

  const addPostcode = (place) => setPostcodes(p => p.some(x => x.postcode === place.postcode) ? p : [...p, place]);
  const removePostcode = code => setPostcodes(p => p.filter(x => x.postcode !== code));

  // Removing a radius pick doesn't replace it. Shuffle gives a new set, so
  // earlier removals no longer apply; extra postcodes stay.
  const removePick = code => setRemovedPicks(p => (p.includes(code) ? p : [...p, code]));
  const shufflePicks = () => { setShuffle(n => n + 1); setRemovedPicks([]); };

  const addSite = () => setSites(s => [...s, { key: Date.now() + Math.random(), text: "", place: null }]);
  const updateSite = (key, fields) => setSites(s => s.map(x => (x.key === key ? { ...x, ...fields } : x)));
  const removeSite = key => setSites(s => s.filter(x => x.key !== key));

  const updateBusiness = (fields) => setBusiness(b => ({ ...b, ...fields }));

  return {
    mode, setMode,
    keywordInput, setKeywordInput, keywords, addKeyword, addKeywords, removeKeyword,
    postcodes, addPostcode, removePostcode,
    sites, addSite, updateSite, removeSite,
    radius, setRadius, shuffle, shufflePicks, removedPicks, removePick,
    nicheChoice, setNicheChoice,
    business, updateBusiness,
    samples, setSamples,
    spacing, setSpacing,
  };
}
